import { GroupSecurity } from '@quromedical/fhir-common';
import type { CreateMedicationUsage } from '@quromedical/models';
import { WithGroup } from 'core/auth';
import { MedicationStatusCard } from 'core/medication';
import { MedicationHistory, Section } from 'design-system';
import { useRevalidation } from 'hooks/useRevalidation';
import { medicationFetcher } from 'integration';
import { MedicationApi } from 'integration/aggregate';
import type React from 'react';
import { useCallback } from 'react';
import { CreateMedicationCard, MedicationTable } from 'screens/medication';
import { strings } from 'strings';

interface PatientMedicationsTabProps {
  patientId: string;
  admissionId?: string;
}
const api = new MedicationApi();

const assignableMedicationFetcher = medicationFetcher('create-usage');

export const PatientMedicationsTab: React.FC<PatientMedicationsTabProps> = ({
  patientId,
  admissionId,
}) => {
  const revalidate = useRevalidation(patientId);

  const onSubmit = useCallback(
    async (medicationUsage: CreateMedicationUsage[]) => {
      if (!admissionId) {
        return;
      }
      await api.createMedication({ medicationUsage, patientId, admissionId });
      revalidate();
    },
    [patientId, admissionId, revalidate],
  );

  const hadAdmission = !!admissionId;

  return (
    <>
      <WithGroup groups={[GroupSecurity.MedicationStatementCreate]}>
        <Section isVisible={!hadAdmission}>
          <MedicationStatusCard
            messages={[strings.WarningNoAdmission]}
            cardLabel={strings.MedicationStatementsStatusCartTitle}
          />
        </Section>
        <Section isVisible={hadAdmission} unsetZIndex>
          <CreateMedicationCard
            onSubmit={onSubmit}
            medicationFetcher={assignableMedicationFetcher}
            title={strings.FormTitleCreateMedicationStatement}
          />
        </Section>
      </WithGroup>

      <MedicationHistory
        patientId={patientId}
        HistoryComponent={MedicationTable}
        groups={[GroupSecurity.MedicationStatementRead]}
      />
    </>
  );
};
